/**
 * Fixed full-viewport background video — the signature J+P motif.
 *
 * Ported from `components/layout/VideoBackground.tsx`. It loops as a "boomerang": forward to
 * the end, then back to the start, then forward again, rather than the hard cut a plain `loop`
 * attribute gives.
 *
 * **The reverse leg is done by hand because no browser will do it.** `playbackRate` accepting a
 * negative number is in the specification, but no shipping engine decodes backwards for one —
 * they either ignore it or freeze the frame. So once `ended` fires, a `requestAnimationFrame`
 * loop steps `currentTime` backwards by the real elapsed time each frame, at the same rate the
 * video played forward, until it reaches zero and hands playback back to a normal `play()`.
 * `background-loop.mp4` is encoded with a keyframe every six frames specifically so those
 * repeated seeks stay cheap; without that, scrubbing backward stutters.
 *
 * Honouring `prefers-reduced-motion` here is the reason the glow on a generated image does not
 * need to: that preference is about motion, and this is the motion.
 */

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";

import { BACKGROUND_VIDEO_SRC } from "@/constants/brand";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

function subscribeToReducedMotion(onChange: () => void): () => void {
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}

/**
 * A media query is an external store, so it is read with `useSyncExternalStore` rather than
 * mirrored into state from an effect. That is not a lint technicality: mirroring means the
 * first paint uses the default and the correct value arrives one render later, which for this
 * particular preference is a frame of unwanted motion shown to somebody who asked for none.
 *
 * The server snapshot returns `true` for the same reason: if the preference is not knowable,
 * the safe answer is "reduce".
 */
function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(
    subscribeToReducedMotion,
    () => window.matchMedia(REDUCED_MOTION_QUERY).matches,
    () => true,
  );
}

export function VideoBackground() {
  const reducedMotion = usePrefersReducedMotion();
  const [failed, setFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const reverseFrameRef = useRef<number | null>(null);

  const stopReverse = useCallback(() => {
    if (reverseFrameRef.current !== null) {
      cancelAnimationFrame(reverseFrameRef.current);
      reverseFrameRef.current = null;
    }
  }, []);

  const playReverse = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Its own timestamp per reverse leg, rather than the one `ended` carried, so the first
    // step's delta is measured from when rewinding actually starts.
    let lastTimestamp: number | null = null;

    const step = (timestamp: number) => {
      if (lastTimestamp === null) lastTimestamp = timestamp;
      const elapsedSeconds = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      const next = video.currentTime - elapsedSeconds;
      if (next <= 0) {
        video.currentTime = 0;
        reverseFrameRef.current = null;
        // Autoplay policies gate only the *first* programmatic play. This continues a video
        // the browser already allowed to autoplay, so an engine that permitted that permits
        // this too.
        void video.play();
        return;
      }

      video.currentTime = next;
      reverseFrameRef.current = requestAnimationFrame(step);
    };

    reverseFrameRef.current = requestAnimationFrame(step);
  }, []);

  const showVideo = !reducedMotion && !failed;

  // Belt and braces: stop an in-flight reverse scrub if the video is hidden mid-playback (a
  // preference change, or an error) or on unmount.
  useEffect(() => {
    if (!showVideo) stopReverse();
    return stopReverse;
  }, [showVideo, stopReverse]);

  return (
    <div aria-hidden="true" className="fixed inset-0 -z-50 bg-jp-ink">
      {showVideo ? (
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          src={BACKGROUND_VIDEO_SRC}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={playReverse}
          onError={() => setFailed(true)}
        />
      ) : null}
      {/*
        A flat dark layer over the video, separate from `text-on-video`'s per-glyph shadow.
        That shadow alone struggles against the video's brighter frames; this lifts the
        baseline contrast for every page rather than relying on it.
      */}
      <div className="absolute inset-0 bg-black/45" />
    </div>
  );
}
